import { useState, useEffect, useCallback, useRef } from "react";
import { ApiHelper } from "../../../utils/ApiHelper";
import { REQUIREMENTS_DATA } from "../../../lib/API_KEYS";

// Format date utility (for consistent formatting)
export const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
};

export const useRFQData = (search, currentPage, perPage, statusFilter = null) => {
    const [loading, setLoading] = useState(false);
    const [requirements, setRequirements] = useState([]);
    const [selectedRows, setSelectedRows] = useState({});
    const [totalResults, setTotalResults] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [availableStatuses, setAvailableStatuses] = useState([]);
    const abortControllerRef = useRef(null);

    const fetchRequirements = useCallback(async () => {
        // Cancel previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        setLoading(true);
        try {
            // Prepare status filter for backend (exclude "All")
            const statusParam = statusFilter && !statusFilter.includes("All") ? { status: statusFilter } : {};

            const response = await new ApiHelper().FetchFromServer(
                ...REQUIREMENTS_DATA,
                {
                    ...(search ? { search } : {}),
                    page: currentPage,
                    limit: perPage,
                    ...statusParam
                }
            );

            // Only update state if this request hasn't been cancelled
            if (!signal.aborted) {
                const results = response.data?.results || [];
                setRequirements(results);
                setTotalResults(response.data?.totalResults || results.length);
                setTotalPages(response.data?.totalPages || 1);
                if (Array.isArray(response.data?.availableStatuses)) {
                    setAvailableStatuses(response.data.availableStatuses);
                }
            }
        } catch (err) {
            // Don't log abort errors
            if (err.name !== 'AbortError') {
                console.error("Error fetching requirements:", err);
                if (!signal.aborted) {
                    setRequirements([]);
                    setTotalResults(0);
                    setTotalPages(1);
                }
            }
        } finally {
            if (!signal.aborted) {
                setLoading(false);
            }
        }
    }, [search, currentPage, perPage, statusFilter]);

    useEffect(() => {
        fetchRequirements();
    }, [search, currentPage, perPage, statusFilter]);

    // --- Selection logic ---
    const startIdx = (currentPage - 1) * perPage;
    const allSelected = requirements.every(
        (_, i) => selectedRows[startIdx + i]
    );

    const toggleAll = (checked) => {
        const updated = { ...selectedRows };
        requirements.forEach((_, i) => {
            updated[startIdx + i] = checked;
        });
        setSelectedRows(updated);
    };

    const toggleRow = (index, checked) => {
        setSelectedRows((prev) => ({ ...prev, [index]: checked }));
    };

    return {
        loading,
        requirements,
        totalResults,
        totalPages,
        availableStatuses,
        selectedRows,
        toggleAll,
        toggleRow,
        formatDate,
    };
};
